/**
 * Cart Drawer Recommendations
 * 
 * Sistema híbrido que usa metacampos para configurar recomendaciones dinámicas
 * basadas en el primer producto del carrito usando la API de Shopify Recommendations.
 * 
 * Metacampos requeridos (en Stores):
 * - custom.habilitar_recomendaciones_en_carrito (boolean)
 * - custom.tipo_de_recomendacion_en_carrito (text: 'related' o 'complementary')
 * - custom.limite_de_productos_recomendados (number: 2-12)
 */

(function() {
  'use strict';

  // Prevenir múltiples inicializaciones
  if (window.CART_RECOMMENDATIONS_INITIALIZED) {
    return;
  }

  window.CART_RECOMMENDATIONS_INITIALIZED = true;

  // Configuración desde metacampos (pasada desde Liquid)
  const config = window.CART_RECOMMENDATIONS_CONFIG || {
    enabled: false,
    type: 'related',
    limit: 5
  };

  // Normalizar valores (asegurar tipos correctos)
  config.enabled = config.enabled === true || config.enabled === 'true' || config.enabled === 1;
  config.type = (config.type || 'related').toLowerCase().trim();
  config.limit = parseInt(config.limit) || 5;
  
  // Validar límite (2-12)
  if (config.limit < 2) config.limit = 2;
  if (config.limit > 12) config.limit = 12;

  // Cache del último HTML exitoso de productos: se reinserta inmediatamente mientras
  // llega el fetch fresco, eliminando el parpadeo de "Cargando recomendaciones...".
  let cachedProductsHTML = null;

  /**
   * Obtiene el primer producto del carrito
   */
  async function getFirstCartProduct() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();
      
      if (!cart.items || cart.items.length === 0) {
        return null;
      }

      const firstItem = cart.items[0];

      return {
        product_id: firstItem.product_id,
        handle: firstItem.handle,
        title: firstItem.product_title
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Obtiene productos recomendados desde la API de Shopify
   */
  async function getRecommendedProducts(productId, intent = 'related', limit = 5) {
    try {
      const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return data.products || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Obtiene IDs de productos ya en el carrito para excluirlos
   */
  async function getCartProductIds() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return [];
      
      const cart = await response.json();
      return cart.items.map(item => item.product_id).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  /**
   * Obtiene datos completos de un producto desde la API
   */
  async function getProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Obtiene el HTML renderizado del precio con descuento usando la API de secciones de Shopify
   * Esto asegura que se incluyan todos los metafields de colección y descuentos
   */
  async function getProductPriceHTML(productHandle) {
    try {
      const url = `/products/${productHandle}?sections=cart-drawer-price-section`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const sectionKeys = Object.keys(data);
      
      // La clave puede ser "cart-drawer-price-section" o "cart-drawer-price-section-0", etc.
      const sectionKey = sectionKeys.find(k => k.startsWith('cart-drawer-price-section'));
      const sectionHTML = sectionKey ? data[sectionKey] : data['cart-drawer-price-section'];
      
      if (!sectionHTML || typeof sectionHTML !== 'string') {
        return null;
      }
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const badgesEl = doc.querySelector('.lop-cart-drawer-discount-badges');
      const badgesHTML = badgesEl ? badgesEl.outerHTML : '';
      const priceWrapper = doc.querySelector('.LOP-product-price-wrapper');

      if (!priceWrapper) {
        return badgesHTML ? { priceHTML: null, badgesHTML } : null;
      }

      const priceElement = priceWrapper.querySelector('.product-price');

      if (priceElement) {
        return { priceHTML: priceElement.outerHTML, badgesHTML };
      }

      return { priceHTML: priceWrapper.innerHTML, badgesHTML };
    } catch (error) {
      return null;
    }
  }

  /**
   * Formatea dinero en formato argentino
   */
  function formatMoney(cents) {
    const amount = Math.round(cents / 100);
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Genera el HTML para un producto del carrusel
   * @param {Object} product - Datos del producto
   * @param {string} buttonText - Texto del botón
   * @param {string} priceHTML - HTML renderizado del precio (opcional, si no se proporciona se calcula manualmente)
   * @param {string} badgesHTML - HTML de cucardas de colección (opcional)
   */
  function generateProductHTML(product, buttonText = 'Añadir al carrito', priceHTML = null, badgesHTML = '') {
    // Manejar imagen
    let imageHTML = '';
    if (product.featured_image) {
      const imageUrl = product.featured_image.startsWith('http') 
        ? product.featured_image 
        : product.featured_image.startsWith('//') 
          ? `https:${product.featured_image}`
          : `${window.location.origin}${product.featured_image}`;
      
      imageHTML = `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy" class="lazyload" style="width: 100%; height: 100%; object-fit: contain;">`;
    } else {
      imageHTML = `<div class="thb-placeholder" style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f5f5f5;">
        <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <rect width="100" height="100" fill="#f5f5f5"/>
          <text x="50" y="50" text-anchor="middle" fill="#999" font-size="12">Sin imagen</text>
        </svg>
      </div>`;
    }

    // Truncar título
    const title = product.title.length > 24 
      ? product.title.substring(0, 24) + '...' 
      : product.title;

    // Obtener variante disponible (necesario para disponibilidad y precio)
    const variant = product.variants?.find(v => v.available) || product.variants?.[0];

    // Si no se proporciona priceHTML renderizado, calcular manualmente (fallback)
    if (!priceHTML) {
      const originalPriceInCents = variant?.price || product.price || 0;
      
      // Intentar calcular descuento basado en metafields de colección (si están disponibles)
      let hasCollectionDiscount = false;
      let discountPercentage = 0;
      let discountedPriceInCents = originalPriceInCents;
      
      if (product.collections && Array.isArray(product.collections)) {
        for (const collection of product.collections) {
          let collectionDiscount = null;
          
          // Intentar diferentes estructuras de metafields
          if (collection.metafields && collection.metafields.custom && collection.metafields.custom.numero_de_descuento) {
            collectionDiscount = collection.metafields.custom.numero_de_descuento;
          } else if (collection.metafields?.custom?.numero_de_descuento) {
            collectionDiscount = collection.metafields.custom.numero_de_descuento;
          } else if (collection.numero_de_descuento) {
            collectionDiscount = collection.numero_de_descuento;
          }
          
          if (collectionDiscount) {
            discountPercentage = parseInt(collectionDiscount) || 0;
            if (discountPercentage > 0) {
              hasCollectionDiscount = true;
              const discountMultiplier = 100 - discountPercentage;
              discountedPriceInCents = Math.round(originalPriceInCents * discountMultiplier / 100);
              break;
            }
          }
        }
      }
      
      // Fallback: usar compare_at_price si no hay descuento de colección
      const compareAtPriceInCents = variant?.compare_at_price || product.compare_at_price || 0;
      const hasCompareAtDiscount = !hasCollectionDiscount && compareAtPriceInCents > originalPriceInCents && compareAtPriceInCents > 0;
      
      // Determinar precios finales
      let finalPriceInCents = originalPriceInCents;
      let originalPriceToShow = originalPriceInCents;
      
      if (hasCollectionDiscount) {
        finalPriceInCents = discountedPriceInCents;
        originalPriceToShow = originalPriceInCents;
      } else if (hasCompareAtDiscount) {
        finalPriceInCents = originalPriceInCents;
        originalPriceToShow = compareAtPriceInCents;
      }
      
      const hasDiscount = hasCollectionDiscount || hasCompareAtDiscount;
      
      // Formatear precios
      const formattedPrice = formatMoney(finalPriceInCents);
      const formattedComparePrice = hasDiscount ? formatMoney(originalPriceToShow) : '';

      // Generar HTML de precio
      if (hasDiscount) {
        priceHTML = `
          <del class="LOP-cart-drawer--carousel-price-compare">${formattedComparePrice}</del>
          <ins class="LOP-cart-drawer--carousel-price-final">${formattedPrice}</ins>
        `;
      } else {
        priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${formattedPrice}</ins>`;
      }
    }
    
    // Convertir el HTML renderizado al formato del carrito SOLO si viene de la API de secciones
    // (contiene .product-price). Si es nuestro fallback ya tiene las clases del carrito, no tocar.
    if (priceHTML && typeof priceHTML === 'string' && priceHTML.trim() && priceHTML.includes('product-price')) {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = priceHTML;
        
        // Buscar precio tachado y precio final dentro del elemento .product-price
        const priceElement = tempDiv.querySelector('.product-price');
        if (priceElement) {
          const strikeElement = priceElement.querySelector('.product-price--strike');
          const saleElement = priceElement.querySelector('.product-price--sale');
          const regularElement = priceElement.querySelector('.product-price--regular:not(.product-price--strike)');
          
          if (strikeElement && saleElement) {
            // Hay descuento: convertir a formato del carrito
            const strikeText = strikeElement.textContent.trim();
            const saleText = saleElement.textContent.trim();
            priceHTML = `
              <del class="LOP-cart-drawer--carousel-price-compare">${escapeHtml(strikeText)}</del>
              <ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(saleText)}</ins>
            `;
          } else if (regularElement) {
            // Sin descuento: solo precio regular
            const regularText = regularElement.textContent.trim();
            priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(regularText)}</ins>`;
          } else if (saleElement && !strikeElement) {
            // Solo precio de venta sin tachado
            const saleText = saleElement.textContent.trim();
            priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(saleText)}</ins>`;
          } else {
            // Si no encontramos elementos específicos, usar el texto completo
            const allText = priceElement.textContent.trim();
            if (allText) {
              priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(allText)}</ins>`;
            } else {
              // Si no hay texto, usar fallback
              priceHTML = null;
            }
          }
        } else {
          priceHTML = null; // Usar fallback si no se encuentra el elemento
        }
      } catch (error) {
        // Usar fallback si hay error
        priceHTML = null;
      }
    }

    // Determinar disponibilidad
    const isAvailable = variant?.available !== false && variant?.id;
    const variantId = variant?.id || product.id;

    return `
      <div class="LOP-cart-drawer--carousel-item">
        <a href="/products/${product.handle}" class="LOP-cart-drawer--carousel-item-link" title="${escapeHtml(title)}">
          <div class="LOP-cart-drawer--carousel-item-image">
            ${badgesHTML ? `<div class="lop-cart-recommended-badges-on-image">${badgesHTML}</div>` : ''}
            ${imageHTML}
          </div>
          <div class="LOP-cart-drawer--carousel-item-info">
            <span class="LOP-cart-drawer--carousel-item-title">${escapeHtml(title)}</span>
            <div class="LOP-cart-drawer--carousel-item-price">
              ${priceHTML}
            </div>
          </div>
        </a>
        ${isAvailable ? `
          <quick-view class="LOP-cart-drawer--carousel-add-btn"
            href="#Product-Drawer"
            data-product-id="${variantId}"
            data-product-handle="${escapeHtml(product.handle)}"
            role="button"
            tabindex="0">
            <span class="LOP-cart-drawer--carousel-add-btn-text">${escapeHtml(buttonText)}</span>
          </quick-view>
        ` : `
          <button class="LOP-cart-drawer--carousel-add-btn" disabled type="button">
            <span class="LOP-cart-drawer--carousel-add-btn-text">Agotado</span>
          </button>
        `}
      </div>
    `;
  }

  /**
   * Función helper para escapar HTML
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Referencias al nodo actual del carrusel. Tras cart:refresh el Section Rendering
   * reemplaza .cart-drawer--inner; si seguimos usando nodos viejos, el carrusel visible queda en "Cargando...".
   */
  function getLiveCarouselRefs() {
    const allCarouselSections = document.querySelectorAll('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
    if (allCarouselSections.length > 1) {
      for (let i = 1; i < allCarouselSections.length; i++) {
        allCarouselSections[i].remove();
      }
    }
    const section = document.querySelector('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
    if (!section || !section.isConnected) {
      return null;
    }
    const track = section.querySelector('.LOP-cart-drawer--carousel-track');
    if (!track || !track.isConnected) {
      return null;
    }
    const container = section.querySelector('.LOP-cart-drawer--carousel-container');
    return { section, track, container };
  }

  function resyncCarouselAfterAsync() {
    const cartDrawer = document.querySelector('#Cart-Drawer');
    if (!cartDrawer || cartDrawer.hasAttribute('inert')) {
      return null;
    }
    const next = getLiveCarouselRefs();
    if (next) {
      return next;
    }
    setTimeout(updateRecommendations, 120);
    return null;
  }

  /**
   * Actualiza el carrusel con productos recomendados
   */
  async function updateRecommendations() {

    // Verificar que esté habilitado
    if (!config.enabled) {
      return;
    }

    // Verificar que el cart drawer esté abierto y visible (sin atributo inert)
    const cartDrawer = document.querySelector('#Cart-Drawer');
    if (!cartDrawer || cartDrawer.hasAttribute('inert')) {
      return;
    }

    // Verificar que el cart drawer esté visible (no display: none)
    const drawerComputedStyle = window.getComputedStyle(cartDrawer);
    if (drawerComputedStyle.display === 'none') {
      // Esperar hasta que el cart drawer sea visible
      let attempts = 0;
      const checkVisibility = setInterval(() => {
        attempts++;
        const currentDrawer = document.querySelector('#Cart-Drawer');
        if (!currentDrawer || currentDrawer.hasAttribute('inert')) {
          clearInterval(checkVisibility);
          return;
        }
        const currentStyle = window.getComputedStyle(currentDrawer);
        if (currentStyle.display !== 'none' || attempts > 20) {
          clearInterval(checkVisibility);
          if (currentStyle.display !== 'none') {
            // Continuar con la actualización después de un breve delay
            setTimeout(updateRecommendations, 100);
          }
        }
      }, 100);
      return;
    }

    const initialRefs = getLiveCarouselRefs();
    if (!initialRefs) {
      return;
    }

    let carouselSection = initialRefs.section;
    let carouselTrack = initialRefs.track;
    let carouselContainer = initialRefs.container;

    // Stale-while-revalidate: si hay un HTML cacheado de la última carga exitosa,
    // insertarlo de inmediato para que el usuario no vea "Cargando recomendaciones..."
    // mientras llega el fetch fresco.
    const sectionHasItems = () =>
      carouselSection.querySelectorAll('.LOP-cart-drawer--carousel-item').length > 0;

    if (!sectionHasItems() && cachedProductsHTML) {
      carouselTrack.innerHTML = cachedProductsHTML;
      carouselSection.style.opacity = '0.6';
      carouselSection.style.pointerEvents = 'none';
      carouselSection.style.transition = 'opacity 0.15s';
    } else if (sectionHasItems()) {
      // El track ya tiene ítems (ej. refresh sin reemplazo DOM): reducir opacidad
      carouselSection.style.opacity = '0.6';
      carouselSection.style.pointerEvents = 'none';
      carouselSection.style.transition = 'opacity 0.15s';
    }
    // Si no hay cache ni ítems, el "Cargando..." del servidor ya está visible: no tocar.

    // Restaura la opacidad del section (usado en salidas tempranas)
    const clearSectionOpacity = () => {
      carouselSection.style.opacity = '';
      carouselSection.style.pointerEvents = '';
      carouselSection.style.transition = '';
    };

    try {
      // 1. Obtener primer producto del carrito y IDs del carrito en paralelo
      const [firstProduct, cartProductIds] = await Promise.all([
        getFirstCartProduct(),
        getCartProductIds()
      ]);

      let r = resyncCarouselAfterAsync();
      if (!r) return;
      carouselSection = r.section;
      carouselTrack = r.track;
      carouselContainer = r.container;
      
      if (!firstProduct) {
        clearSectionOpacity();
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">Agrega productos al carrito para ver recomendaciones</div>';
        return;
      }

      // 2. Obtener productos recomendados
      const recommendedProducts = await getRecommendedProducts(
        firstProduct.product_id,
        config.type,
        config.limit
      );

      r = resyncCarouselAfterAsync();
      if (!r) return;
      carouselSection = r.section;
      carouselTrack = r.track;
      carouselContainer = r.container;

      if (recommendedProducts.length === 0) {
        clearSectionOpacity();
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay recomendaciones disponibles</div>';
        return;
      }

      // 4. Filtrar productos ya en el carrito
      const filteredProducts = recommendedProducts.filter(product => {
        // Excluir si ya está en el carrito
        if (cartProductIds.includes(product.id)) {
          return false;
        }
        return true;
      });

      if (filteredProducts.length === 0) {
        clearSectionOpacity();
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay productos recomendados disponibles</div>';
        return;
      }

      // 5. Obtener datos completos de productos (en paralelo, optimizado)
      // Limitar a máximo config.limit + 2 para tener margen
      const productsToFetch = filteredProducts.slice(0, Math.min(config.limit + 2, filteredProducts.length));
      
      // Hacer todas las llamadas en paralelo (más rápido)
      // Usar Promise.allSettled para que si una falla, las demás continúen
      const productDataPromises = productsToFetch.map(product =>
        getProductData(product.handle).catch(() => null)
      );
      const productDataResults = await Promise.allSettled(productDataPromises);

      r = resyncCarouselAfterAsync();
      if (!r) return;
      carouselSection = r.section;
      carouselTrack = r.track;
      carouselContainer = r.container;

      const productsWithData = productDataResults
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .map(result => result.value);

      if (productsWithData.length === 0) {
        clearSectionOpacity();
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay productos recomendados disponibles</div>';
        return;
      }

      // 6. Ordenar por precio (mayor a menor)
      productsWithData.sort((a, b) => {
        const variantA = a.variants?.find(v => v.available) || a.variants?.[0];
        const variantB = b.variants?.find(v => v.available) || b.variants?.[0];
        const priceA = variantA?.price || a.price || 0;
        const priceB = variantB?.price || b.price || 0;
        return priceB - priceA;
      });

      // Limitar a la cantidad configurada
      const finalProducts = productsWithData.slice(0, config.limit);

      // 7. Obtener precios renderizados con descuentos (metafields de colección) vía API de secciones
      const priceHTMLMap = {};
      
      try {
        const priceHTMLPromises = finalProducts.map(product =>
          getProductPriceHTML(product.handle).catch(() => null)
        );
        const priceHTMLResults = await Promise.allSettled(priceHTMLPromises);
        
        finalProducts.forEach((product, index) => {
          const result = priceHTMLResults[index];
          if (result.status === 'fulfilled' && result.value) {
            priceHTMLMap[product.handle] = result.value;
          } else {
            priceHTMLMap[product.handle] = null;
          }
        });
      } catch (error) {
        finalProducts.forEach(product => {
          priceHTMLMap[product.handle] = null;
        });
      }

      r = resyncCarouselAfterAsync();
      if (!r) return;
      carouselSection = r.section;
      carouselTrack = r.track;
      carouselContainer = r.container;

      // 8. Obtener texto del botón desde settings
      // Intentar obtener desde un elemento existente o usar default
      let buttonText = 'Añadir al carrito';
      const existingButton = document.querySelector('.LOP-cart-drawer--carousel-add-btn-text');
      if (existingButton) {
        buttonText = existingButton.textContent.trim() || 'Añadir al carrito';
      } else if (window.LOP_THEME_SETTINGS && window.LOP_THEME_SETTINGS.cartCarouselButtonText) {
        buttonText = window.LOP_THEME_SETTINGS.cartCarouselButtonText;
      }

      // 9. Generar HTML con precios renderizados
      const productsHTML = finalProducts.map(product => {
        try {
          if (!product || !product.handle) {
            return ''; // Saltar productos inválidos
          }
          const pricePayload = priceHTMLMap[product.handle] || null;
          const priceHTML = pricePayload && typeof pricePayload === 'object'
            ? pricePayload.priceHTML
            : pricePayload;
          const badgesHTML = pricePayload && typeof pricePayload === 'object'
            ? (pricePayload.badgesHTML || '')
            : '';
          const html = generateProductHTML(product, buttonText, priceHTML, badgesHTML);
          if (!html || html.trim() === '') {
            return ''; // Saltar si el HTML está vacío
          }
          return html;
        } catch (error) {
          // Intentar generar HTML básico como fallback
          try {
            return generateProductHTML(product, buttonText, null);
          } catch (fallbackError) {
            return ''; // Saltar este producto completamente
          }
        }
      }).filter(html => html && html.trim() !== '').join(''); // Filtrar HTMLs vacíos

      // Verificar que tenemos HTML válido
      if (!productsHTML || productsHTML.trim() === '') {
        clearSectionOpacity();
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay productos recomendados disponibles</div>';
        return;
      }

      // 10. Insertar HTML en el track vivo (puede haberse regenerado durante los awaits previos)
      r = resyncCarouselAfterAsync();
      if (!r) return;
      carouselSection = r.section;
      carouselTrack = r.track;
      carouselContainer = r.container;

      // 11. Insertar HTML y actualizar cache para próximas actualizaciones
      try {
        carouselTrack.innerHTML = productsHTML;
        cachedProductsHTML = productsHTML;
      } catch (error) {
        throw error; // Re-lanzar para que sea capturado por el catch principal
      }
      
      // Verificar que los items se insertaron correctamente
      const insertedItems = carouselTrack.querySelectorAll('.LOP-cart-drawer--carousel-item');
      
      if (insertedItems.length === 0) {
        return;
      }
      
      // 12. Forzar visibilidad y recálculo de dimensiones
      carouselTrack.style.display = 'flex';
      carouselTrack.style.visibility = 'visible';
      carouselTrack.style.opacity = '';

      // Restaurar sección con transición suave (limpia el estado de carga)
      carouselSection.style.display = 'block';
      carouselSection.style.visibility = 'visible';
      void carouselSection.offsetWidth; // forzar reflow para que la transición sea visible
      carouselSection.style.opacity = '';
      carouselSection.style.pointerEvents = '';
      // Limpiar transition después de que termine
      setTimeout(() => { carouselSection.style.transition = ''; }, 200);

      if (carouselContainer) {
        carouselContainer.style.display = 'block';
        carouselContainer.style.visibility = 'visible';
        carouselContainer.style.opacity = '';
      }
      
      // Forzar recálculo de layout
      void carouselTrack.offsetWidth;
      void carouselSection.offsetWidth;
      if (carouselContainer) {
        void carouselContainer.offsetWidth;
      }
      
      // 13. Inicializar navegación del carrusel
      const prevButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-prev');
      const nextButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-next');
      
      // Función para actualizar el estado de los botones
      const updateNavButtons = () => {
        if (!carouselContainer) return;
        const scrollLeft = carouselContainer.scrollLeft;
        const scrollWidth = carouselContainer.scrollWidth;
        const clientWidth = carouselContainer.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        
        if (prevButton) prevButton.disabled = scrollLeft <= 0;
        if (nextButton) nextButton.disabled = scrollLeft >= maxScroll - 1;
      };
      
      // Configurar botones de navegación
      if (prevButton) {
        prevButton.addEventListener('click', () => {
          if (!carouselContainer) return;
          const itemWidth = insertedItems[0]?.offsetWidth || 177;
          carouselContainer.scrollBy({ left: -itemWidth * 3, behavior: 'smooth' });
        });
      }
      
      if (nextButton) {
        nextButton.addEventListener('click', () => {
          if (!carouselContainer) return;
          const itemWidth = insertedItems[0]?.offsetWidth || 177;
          carouselContainer.scrollBy({ left: itemWidth * 3, behavior: 'smooth' });
        });
      }
      
      // Actualizar botones cuando se hace scroll
      if (carouselContainer) {
        carouselContainer.addEventListener('scroll', updateNavButtons);
        updateNavButtons(); // Estado inicial
      }
      

    } catch {
      const errorRefs = getLiveCarouselRefs();
      if (errorRefs) {
        errorRefs.section.style.opacity = '';
        errorRefs.section.style.pointerEvents = '';
        errorRefs.section.style.transition = '';
        errorRefs.track.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #d32f2f;">Error al cargar recomendaciones</div>';
      }
    }
  }


  /**
   * Inicializa el sistema de recomendaciones
   */
  function initialize() {
    // Solo inicializar si está habilitado
    if (!config.enabled) {
      return;
    }

    let isUpdating = false;
    let lastUpdateTime = 0;
    let pendingUpdate = false;
    const UPDATE_DEBOUNCE = 300;

    // Función para actualizar con debounce
    function triggerUpdate() {
      const now = Date.now();
      if (isUpdating) {
        pendingUpdate = true;
        return;
      }
      if (now - lastUpdateTime < UPDATE_DEBOUNCE) {
        return;
      }
      lastUpdateTime = now;
      isUpdating = true;

      updateRecommendations().finally(() => {
        isUpdating = false;
        if (pendingUpdate) {
          pendingUpdate = false;
          lastUpdateTime = 0;
          setTimeout(triggerUpdate, 50);
        }
      });
    }

    // Estrategia 1: Escuchar evento cart-drawer:open del custom element
    document.addEventListener('cart-drawer:open', () => {
      setTimeout(triggerUpdate, 400);
    });
    
    // También interceptar el método open() del custom element si existe
    const cartDrawerElement = document.getElementById('Cart-Drawer');
    if (cartDrawerElement && typeof cartDrawerElement.open === 'function') {
      const originalOpen = cartDrawerElement.open.bind(cartDrawerElement);
      cartDrawerElement.open = function() {
        const result = originalOpen.apply(this, arguments);
        setTimeout(() => {
          triggerUpdate();
        }, 400);
        return result;
      };
    }

    // Estrategia 2: MutationObserver para detectar cuando se remueve el atributo 'inert'
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'inert') {
          const target = mutation.target;
          if (target.id === 'Cart-Drawer' && !target.hasAttribute('inert')) {
            setTimeout(triggerUpdate, 400);
          }
        }

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const cartDrawer = node.id === 'Cart-Drawer' 
                ? node 
                : node.querySelector?.('#Cart-Drawer');

              if (cartDrawer && !cartDrawer.hasAttribute('inert')) {
                triggerUpdate();
              }

              if (node.classList?.contains('LOP-cart-drawer--carousel-section') && 
                  node.dataset.recommendationsMode === 'true') {
                triggerUpdate();
              }
            }
          });
        }
      });
    });

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Estrategia 3: Escuchar eventos de actualización del carrito
    document.addEventListener('line-item:change:end', () => {
      setTimeout(triggerUpdate, 400);
    });
    
    document.addEventListener('cart:refresh', () => {
      setTimeout(triggerUpdate, 400);
    });

    // Exponer función globalmente
    window.LOP = window.LOP || {};
    window.LOP.updateRecommendations = updateRecommendations;
    window.LOP.triggerRecommendationsUpdate = triggerUpdate;

    // Función de test
    window.testRecommendations = function() {
      updateRecommendations();
    };

  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

})();
